/**
Author - Venkatesh Pullaganti😃
Git - https://github.com/venkatesh1146
**/

import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import {AA_STATUS_MESSAGES, AA_STATUS_MESSAGES_ALL_FI, VIEW_STATES} from 'constants/AAConstants';
import {KYC_FLOWS} from 'constants/FlowConstants';
import {SkipFlow} from 'screens/FnoDeclarations/components';
import {LoadingView, InitialChoiceView, StatusView, FailureView, PollingView} from './components';
import {shouldShowRetryOption} from './services/consentService';

const propTypes = {
  viewState: PropTypes.string.isRequired,
  consent: PropTypes.shape({
    status: PropTypes.string,
    processingStatus: PropTypes.string,
    errorMessage: PropTypes.string,
    errorCode: PropTypes.string,
  }).isRequired,
  isLoading: PropTypes.bool.isRequired,
  handleContinue: PropTypes.func.isRequired,
  handleSkip: PropTypes.func.isRequired,
  handleUploadIncome: PropTypes.func.isRequired,
  handleRetry: PropTypes.func.isRequired,
  flow: PropTypes.string,
};

const getStatusMessage = (consentStatus, processingStatus, errorCode, flow) => {
  const messages =
    flow === KYC_FLOWS.ALL_FI
      ? {...AA_STATUS_MESSAGES, ...AA_STATUS_MESSAGES_ALL_FI}
      : AA_STATUS_MESSAGES;

  if (errorCode === 'create_new') {
    return messages.CREATE_NEW;
  }

  if (consentStatus === 'ACTIVE' && ['FETCHING', 'PROCESSING'].includes(processingStatus)) {
    return messages.ACTIVE_FETCHING;
  }

  switch (consentStatus) {
    case 'REJECTED':
      return messages.REJECTED;
    case 'EXPIRED':
      return messages.EXPIRED;
    case 'PENDING':
      return messages.PENDING;
    default:
      break;
  }

  switch (processingStatus) {
    case 'FAILED':
      return messages.FAILED;
    default:
      return null;
  }
};

const AccountAggregator = ({
  viewState,
  consent,
  isLoading,
  handleContinue,
  handleSkip,
  handleUploadIncome,
  handleRetry,
  flow,
}) => {
  const isAllFi = flow === KYC_FLOWS.ALL_FI;
  const handleInitialSubmit = (action) => (action === 'continue' ? handleContinue() : handleSkip());

  const statusMessage = getStatusMessage(
    consent.status,
    consent.processingStatus,
    consent.errorCode,
    flow
  );

  const renderView = () => {
    switch (viewState) {
      case VIEW_STATES.LOADING:
        return <LoadingView />;

      case VIEW_STATES.INITIAL:
        return (
          <InitialChoiceView isLoading={isLoading} onSubmit={handleInitialSubmit} flow={flow} />
        );

      case VIEW_STATES.PENDING:
        return (
          <StatusView
            statusMessage={statusMessage}
            errorMessage={consent.errorMessage}
            showUploadButton={!isAllFi}
            onUpload={handleUploadIncome}
            isLoading={isLoading}
          />
        );

      case VIEW_STATES.PROCESSING:
        return (
          <StatusView
            statusMessage={statusMessage}
            errorMessage={consent.errorMessage}
            showUploadButton={false}
            onUpload={handleUploadIncome}
            isLoading={isLoading}
          />
        );

      case VIEW_STATES.POLLING:
        return (
          <StatusView
            statusMessage={
              isAllFi
                ? AA_STATUS_MESSAGES_ALL_FI.ACTIVE_WAITING_FOR_CONSENT_APPROVAL
                : AA_STATUS_MESSAGES.ACTIVE_WAITING_FOR_CONSENT_APPROVAL
            }
            errorMessage={consent.errorMessage}
            showUploadButton={false}
            onUpload={handleUploadIncome}
            isLoading={isLoading}
            showRefreshButton
          />
        );

      case VIEW_STATES.FAILED:
        return (
          <FailureView
            statusMessage={statusMessage}
            errorMessage={consent.errorMessage}
            onUpload={handleUploadIncome}
            onRetry={handleRetry}
            isLoading={isLoading}
            showRetryOption={isAllFi || shouldShowRetryOption(consent.status)}
            flow={flow}
          />
        );

      case VIEW_STATES.SKIP_FLOW:
        return <SkipFlow />;

      default:
        return <InitialChoiceView isLoading={isLoading} onSubmit={handleInitialSubmit} />;
    }
  };

  return (
    <AAScreenWrapper>
      <Container>
        <DataContainer>{renderView()}</DataContainer>
      </Container>
    </AAScreenWrapper>
  );
};

const AAScreenWrapper = styled.div`
  height: 100vh;
  width: 100%;
  scroll-behavior: scroll;
  position: relative;
  overflow-y: scroll;

  @media (max-width: 768px) {
    background: #fff;
  }
`;

const Container = styled.div`
  padding: 2.4rem 0;
  width: 90%;
  margin: 0 auto;
`;

const DataContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
`;

AccountAggregator.propTypes = propTypes;

export default AccountAggregator;
